/** Módulo productos: listado con filtros + ficha (presentaciones, matriz de precios, stock, mínimos, histórico). */
import { formatearCantidad, formatearTasa, FRACCION_ETIQUETA, FRACCIONES_MINIMAS, Money, productoCrearInput, UNIDADES, type EstadoMinimo, type ProductoCrearEntrada, type ProductoCrearInput, type ProductoDetalleDTO, type ProductoResumenDTO, type StockAlmacenDTO, type TasaDTO } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Layers, Lock, Package, Percent, Plus, Search, FileSpreadsheet } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo, Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { ImportarExcel } from '@/componentes/ImportarExcel.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { veTasa2 } from '@/lib/tasa2.ts';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import {
  useActualizarPrecios,
  useCategorias,
  useCrearPresentacion,
  useCrearProducto,
  useEditarPresentacion,
  useEditarProducto,
  useFijarMinimo,
  useFijarMinimoAlmacen,
  useHistoricoPrecios,
  useListasPrecio,
  useProducto,
  useProductos,
} from './api.ts';
import { FichaCosto } from '@/modulos/costos/FichaCosto.tsx';
import { DialogDescuentos } from './Descuentos.tsx';

/** Fracción de la ficha ("0.50") → clave del selector ("0.5"). */
const claveFraccion = (v: string) => FRACCIONES_MINIMAS.find((f) => Number(f) === Number(v)) ?? '1';
const CLASE_MINIMO: Record<EstadoMinimo, string> = { bajo: 'font-semibold text-mg-rojo', cerca: 'font-medium text-amber-700', ok: '', sin_minimo: '' };
/** Productos por página en el selector de la pieza suelta. */
const LIMITE_PIEZAS = 50;

const cant = (v: string | null | undefined) => (v === null || v === undefined ? '—' : formatearCantidad(Money.parse(v)));

function useTasas() {
  const t = useQuery({ queryKey: claves.tasaVigente, queryFn: () => get<TasaDTO>('/tasas/vigente') });
  return t.data ? { tasa_bcv: t.data.bcv, tasa_paralela: t.data.paralela, factor_divisa: t.data.factor_divisa } : undefined;
}


// ─── Listado ───────────────────────────────────────────────────────────────
export function Productos() {
  const { puede, usuario } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [bajoMinimo, setBajoMinimo] = useState(false);
  const [inactivos, setInactivos] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [nuevo, setNuevo] = useState(false);
  const [verDescuentos, setVerDescuentos] = useState(false);
  const filtros = { q: q || undefined, categoria_id: categoria || undefined, bajo_minimo: bajoMinimo ? ('true' as const) : undefined, activo: inactivos ? ('todos' as const) : ('true' as const), pagina, limite: 25 };
  const lista = useProductos(filtros);
  const categorias = useCategorias();
  const tasas = useTasas();
  const puedeEditar = puede('productos.precios');
  const [importar, setImportar] = useState(false);
  const qc = useQueryClient();

  return (
    <>
      <TituloPagina
        titulo="Productos"
        subtitulo={usuario?.sucursal_nombre ? `Disponible de ${usuario.sucursal_nombre} · precios de la lista detal en $ y Bs referenciales` : 'Catálogo global'}
        acciones={
          <>
            <Button variant="outline" onClick={() => setVerDescuentos(true)} data-testid="btn-descuentos">
              <Percent /> Descuentos
            </Button>
            <Button variant="outline" onClick={() => navigate('/productos/combos')} data-testid="btn-combos">
              <Layers /> Combos y recetas
            </Button>
            {puedeEditar ? (
              <Button variant="outline" onClick={() => setImportar(true)} data-testid="btn-importar-productos">
                <FileSpreadsheet /> Cargar Excel
              </Button>
            ) : null}
            {puedeEditar ? (
              <Button onClick={() => setNuevo(true)} data-testid="btn-nuevo-producto">
                <Plus /> Nuevo producto
              </Button>
            ) : null}
          </>
        }
      />
      <ImportarExcel que="productos" tema="productos.excel" abierto={importar} cerrar={() => setImportar(false)} alCargar={() => void qc.invalidateQueries({ queryKey: ['productos'] })} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por código, nombre o marca…" value={q} onChange={(e) => { setQ(e.target.value); setPagina(1); }} className="w-80 pl-8" autoFocus data-testid="buscar-producto" />
        </div>
        <Filtro etiqueta="Categoría">
          <Select value={categoria} onChange={(e) => { setCategoria(e.target.value); setPagina(1); }} className="w-56" aria-label="Categoría" data-testid="filtro-categoria">
            <option value="">Todas las categorías</option>
            {(categorias.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.padre_id ? `— ${c.nombre}` : c.nombre}
              </option>
            ))}
          </Select>
        </Filtro>
        {usuario?.sucursal_id ? (
          <label className="flex h-10 items-center gap-1.5 text-sm">
            <input type="checkbox" checked={bajoMinimo} onChange={(e) => { setBajoMinimo(e.target.checked); setPagina(1); }} /> Bajo mínimo
          </label>
        ) : null}
        <label className="flex h-10 items-center gap-1.5 text-sm">
          <input type="checkbox" checked={inactivos} onChange={(e) => { setInactivos(e.target.checked); setPagina(1); }} /> Incluir inactivos
        </label>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio icono={<Package className="h-6 w-6" />} titulo="Sin productos" descripcion={q ? `Nada coincide con "${q}".` : 'Crea el primer producto del catálogo.'} accion={puedeEditar && !q ? <Button onClick={() => setNuevo(true)}>Nuevo producto</Button> : undefined} />
          ) : (
            <div className="space-y-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Unidad</TableHead>
                    {usuario?.sucursal_id ? <TableHead className="text-right">Disponible</TableHead> : null}
                    <TableHead className="text-right">Precio detal (unidad)</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/productos/${p.id}`)} data-testid={`producto-${p.codigo}`}>
                      <TableCell className="font-mono text-xs">{p.codigo}</TableCell>
                      <TableCell>
                        <p className="font-medium">{p.nombre}</p>
                        <p className="text-[11px] text-muted-foreground">{[p.marca, p.color].filter(Boolean).join(' · ')}</p>
                      </TableCell>
                      <TableCell className="text-xs">{[p.categoria, p.subcategoria].filter(Boolean).join(' › ') || '—'}</TableCell>
                      <TableCell>
                        {p.unidad}
                        {p.fragil ? <Badge variant="critico" className="ml-1">Frágil</Badge> : null}
                        {p.tipo === 'servicio' ? <Badge variant="info" className="ml-1">Servicio</Badge> : null}
                      </TableCell>
                      {usuario?.sucursal_id ? (
                        <TableCell className="text-right num">
                          {p.tipo === 'servicio' ? <span className="text-muted-foreground" title="Los servicios no llevan inventario">sin inventario</span> : cant(p.disponible_sucursal)}
                          {p.bajo_minimo ? (
                            <Badge variant="pendiente" className="ml-1" title={`Mínimo ${p.minimo_sucursal}`}>
                              <AlertTriangle className="mr-1 h-3 w-3" /> mín. {cant(p.minimo_sucursal)}
                            </Badge>
                          ) : null}
                          {/* Disponible por almacén (tienda y depósitos): pedido del cliente 03-10-2026 */}
                          {p.stock_por_almacen && p.stock_por_almacen.length > 1 ? (
                            <p className="text-[11px] font-normal text-muted-foreground" data-testid={`producto-almacenes-${p.codigo}`}>
                              {p.stock_por_almacen.map((s, i) => (
                                <span key={s.almacen_id} className={CLASE_MINIMO[s.estado_minimo]} title={s.minimo ? `Mínimo ${cant(s.minimo)}${s.estado_minimo === 'bajo' ? ' · por debajo' : s.estado_minimo === 'cerca' ? ' · cerca' : ''}` : undefined}>
                                  {i > 0 ? ' · ' : ''}
                                  {s.almacen_nombre}: {cant(s.disponible)}
                                </span>
                              ))}
                            </p>
                          ) : null}
                        </TableCell>
                      ) : null}
                      <TableCell className="text-right">{p.precio_unitario_divisa ? <Dinero valor={p.precio_unitario_divisa} referencial={tasas} /> : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={p.activo ? 'ok' : 'neutro'}>{p.activo ? 'Activo' : 'Inactivo'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{d.total} productos</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                    Anterior
                  </Button>
                  <Button size="sm" variant="outline" disabled={pagina * d.limite >= d.total} onClick={() => setPagina((p) => p + 1)}>
                    Siguiente
                  </Button>
                </div>
              </div>
            </div>
          )
        }
      </EstadoQuery>
      <DialogProducto abierto={nuevo} cerrar={() => setNuevo(false)} />
      <DialogDescuentos abierto={verDescuentos} cerrar={() => setVerDescuentos(false)} />
    </>
  );
}

// ─── Diálogo crear / editar ficha ──────────────────────────────────────────
function DialogProducto({ abierto, cerrar, producto }: { abierto: boolean; cerrar: () => void; producto?: ProductoDetalleDTO }) {
  const navigate = useNavigate();
  const categorias = useCategorias();
  const crear = useCrearProducto();
  const editar = useEditarProducto(producto?.id ?? '');
  const form = useForm<ProductoCrearEntrada, unknown, ProductoCrearInput>({
    resolver: zodResolver(productoCrearInput),
    ...(producto
      ? {
          values: {
            codigo: producto.codigo,
            nombre: producto.nombre,
            descripcion: producto.descripcion,
            categoria_id: producto.categoria_id,
            subcategoria_id: producto.subcategoria_id,
            color: producto.color,
            marca: producto.marca,
            unidad: producto.unidad as ProductoCrearInput['unidad'],
            fragil: producto.fragil,
            despiece_producto_id: producto.despiece_producto_id,
            despiece_factor: producto.despiece_factor,
            tipo: producto.tipo,
            fraccion_minima: claveFraccion(producto.fraccion_minima),
            lamina_largo_mm: producto.lamina_largo_mm,
            lamina_ancho_mm: producto.lamina_ancho_mm,
            lamina_espesor_mm: producto.lamina_espesor_mm,
          },
        }
      : {}),
    defaultValues: { codigo: '', nombre: '', descripcion: '', categoria_id: null, subcategoria_id: null, color: null, marca: null, unidad: 'uni', fragil: false, despiece_producto_id: null, despiece_factor: null, tipo: 'producto', lamina_largo_mm: null, lamina_ancho_mm: null, lamina_espesor_mm: null },
  });
  const categoriaId = useWatch({ control: form.control, name: 'categoria_id' });
  const fragil = useWatch({ control: form.control, name: 'fragil' });
  const tipo = useWatch({ control: form.control, name: 'tipo' });
  // Selector del producto "pieza suelta" (antes pedía el UUID a mano)
  const [qPieza, setQPieza] = useState('');
  // De 50 en 50 con paginador debajo de la lista (pedido de la dueña 2026-10-20)
  const [paginaPieza, setPaginaPieza] = usePaginaBusqueda(qPieza);
  const piezas = useProductos({ q: qPieza || undefined, activo: 'true', pagina: paginaPieza, limite: LIMITE_PIEZAS });
  const piezasPagina = piezas.data?.datos ?? [];
  const enPagina = (pid: string) => piezasPagina.some((p) => p.id === pid);
  // La pieza elegida sigue en la lista (y seleccionada) aunque se pase de página o se busque otra cosa
  const despiezaEn = useWatch({ control: form.control, name: 'despiece_producto_id' });
  const [piezaElegida, setPiezaElegida] = useState<{ id: string; etiqueta: string } | null>(null);
  const limpiar = (d: ProductoCrearInput): ProductoCrearInput => ({
    ...d,
    categoria_id: d.categoria_id || null,
    subcategoria_id: d.subcategoria_id || null,
    color: d.color || null,
    marca: d.marca || null,
    despiece_producto_id: d.despiece_producto_id || null,
    despiece_factor: d.despiece_factor || null,
    lamina_espesor_mm: d.lamina_espesor_mm || null,
  });
  const enviar = form.handleSubmit(async (d) => {
    try {
      if (producto) {
        await editar.mutateAsync(limpiar(d));
        toast.success('Ficha actualizada');
      } else {
        const creado = await crear.mutateAsync(limpiar(d));
        toast.success(`Producto ${creado.codigo} creado`, { description: 'Ahora define presentaciones y precios.' });
        navigate(`/productos/${creado.id}`);
      }
      cerrar();
    } catch (e) {
      toast.error('No se pudo guardar', { description: mensajeDeError(e) });
    }
  });
  const e = form.formState.errors;
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={enviar} noValidate className="space-y-4" data-testid="form-producto">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>{producto ? `Editar ${producto.codigo}` : 'Nuevo producto'}</DialogTitle><AyudaVentana tema="productos.formulario" /></div>
            <DialogDescription>Código único, nombre, descripción, categoría, color, marca y unidad. El costo real lo mueven las compras y los gastos repartidos al costo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <Campo etiqueta="Código" htmlFor="p-codigo" error={e.codigo?.message}>
              <Input id="p-codigo" {...form.register('codigo')} className="uppercase" />
            </Campo>
            <Campo etiqueta="Nombre" htmlFor="p-nombre" error={e.nombre?.message} className="col-span-2">
              <Input id="p-nombre" {...form.register('nombre')} />
            </Campo>
          </div>
          <Campo etiqueta="Descripción" htmlFor="p-desc" error={e.descripcion?.message}>
            <Textarea id="p-desc" rows={2} {...form.register('descripcion')} />
          </Campo>
          <div className="grid grid-cols-4 gap-3">
            <Campo etiqueta="Categoría" htmlFor="p-cat">
              <Select id="p-cat" {...form.register('categoria_id')}>
                <option value="">—</option>
                {(categorias.data ?? []).filter((c) => !c.padre_id).map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Subcategoría" htmlFor="p-sub">
              <Select id="p-sub" {...form.register('subcategoria_id')}>
                <option value="">—</option>
                {(categorias.data ?? []).filter((c) => c.padre_id && c.padre_id === categoriaId).map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Color" htmlFor="p-color">
              <Input id="p-color" {...form.register('color')} />
            </Campo>
            <Campo etiqueta="Marca" htmlFor="p-marca">
              <Input id="p-marca" {...form.register('marca')} />
            </Campo>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Campo etiqueta="Tipo" htmlFor="p-tipo" ayuda={tipo === 'servicio' ? 'Sin inventario: no reserva, no se entrega ni entra a conteos' : 'Un servicio se factura igual pero no lleva inventario'} className="col-span-2">
              <Select id="p-tipo" {...form.register('tipo')} data-testid="p-tipo">
                <option value="producto">Producto (lleva inventario)</option>
                <option value="servicio">Servicio (corte, flete, instalación…)</option>
              </Select>
            </Campo>
            <Campo etiqueta="Se vende en" htmlFor="p-fraccion" error={e.fraccion_minima?.message} ayuda="Fracción mínima de la unidad que se puede vender" className="col-span-2">
              <Select id="p-fraccion" {...form.register('fraccion_minima', { setValueAs: (v: string) => v || undefined })} data-testid="p-fraccion">
                {producto ? null : <option value="">Según la unidad (enteros; centésimas para kg, mts, m2 y lt)</option>}
                {FRACCIONES_MINIMAS.map((f) => (
                  <option key={f} value={f}>{FRACCION_ETIQUETA[f]}</option>
                ))}
              </Select>
            </Campo>
          </div>
          {/* Lámina de vidrio: con su medida se revisa que las piezas de una rotura quepan (respuesta de la dueña del 2026-10-15) */}
          <div className="grid grid-cols-4 items-start gap-3">
            <p className="pt-6 text-xs text-muted-foreground">Lámina de vidrio: medida y espesor (para reportar roturas)</p>
            <Campo etiqueta="Largo (mm)" htmlFor="p-lam-largo" error={e.lamina_largo_mm?.message}>
              <Input id="p-lam-largo" inputMode="numeric" placeholder="3660" {...form.register('lamina_largo_mm', { setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(/\D/g, ''))) })} data-testid="p-lam-largo" />
            </Campo>
            <Campo etiqueta="Ancho (mm)" htmlFor="p-lam-ancho" error={e.lamina_ancho_mm?.message}>
              <Input id="p-lam-ancho" inputMode="numeric" placeholder="2440" {...form.register('lamina_ancho_mm', { setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(/\D/g, ''))) })} data-testid="p-lam-ancho" />
            </Campo>
            <Campo etiqueta="Espesor (mm)" htmlFor="p-lam-espesor" error={e.lamina_espesor_mm?.message}>
              <Input id="p-lam-espesor" inputMode="decimal" placeholder="3" {...form.register('lamina_espesor_mm', { setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : String(v).replace(',', '.')) })} data-testid="p-lam-espesor" />
            </Campo>
          </div>
          <div className="grid grid-cols-4 items-end gap-3">
            <Campo etiqueta="Unidad" htmlFor="p-unidad" error={e.unidad?.message}>
              <Select id="p-unidad" {...form.register('unidad')}>
                {UNIDADES.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Select>
            </Campo>
            <label className="flex h-10 items-center gap-2 text-sm">
              <input type="checkbox" {...form.register('fragil')} /> Frágil (despiece)
            </label>
            {fragil ? (
              <>
                <Campo etiqueta="Producto pieza suelta" htmlFor="p-desp" error={e.despiece_producto_id?.message} ayuda="Al despiezar, cada caja se convierte en N piezas de este producto">
                  <div className="space-y-1">
                    <Input placeholder="Buscar por código o nombre…" value={qPieza} onChange={(ev) => setQPieza(ev.target.value)} className="h-8" data-testid="p-desp-buscar" />
                    <Select
                      id="p-desp"
                      {...form.register('despiece_producto_id', {
                        onChange: (ev: ChangeEvent<HTMLSelectElement>) => {
                          const p = piezasPagina.find((x) => x.id === ev.target.value);
                          setPiezaElegida(p ? { id: p.id, etiqueta: `${p.codigo} · ${p.nombre}` } : null);
                        },
                      })}
                      value={despiezaEn ?? ''}
                      aria-invalid={!!e.despiece_producto_id}
                      data-testid="p-desp"
                    >
                      <option value="">Elige el producto…</option>
                      {producto?.despiece_producto_id && !enPagina(producto.despiece_producto_id) ? (
                        <option value={producto.despiece_producto_id}>{producto.despiece_producto_nombre ?? 'Producto actual'}</option>
                      ) : null}
                      {piezaElegida && piezaElegida.id === despiezaEn && piezaElegida.id !== producto?.despiece_producto_id && !enPagina(piezaElegida.id) ? <option value={piezaElegida.id}>{piezaElegida.etiqueta}</option> : null}
                      {piezasPagina
                        .filter((p) => p.id !== producto?.id)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.codigo} · {p.nombre}
                          </option>
                        ))}
                    </Select>
                    <PaginadorBusqueda pagina={paginaPieza} limite={LIMITE_PIEZAS} total={piezas.data?.total ?? 0} onPagina={setPaginaPieza} testid="p-desp" className="rounded-lg border" />
                  </div>
                </Campo>
                <Campo etiqueta="Piezas por caja" htmlFor="p-factor" error={e.despiece_factor?.message}>
                  <Input id="p-factor" inputMode="decimal" placeholder="4" {...form.register('despiece_factor')} />
                </Campo>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrar}>Cancelar</Button>
            <Button type="submit" cargando={crear.isPending || editar.isPending} data-testid="guardar-producto">
              {producto ? 'Guardar cambios' : 'Crear producto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Ficha ─────────────────────────────────────────────────────────────────
export function ProductoFicha() {
  const { id } = useParams<{ id: string }>();
  const { puede, usuario } = useAuth();
  const navigate = useNavigate();
  const producto = useProducto(id ?? null);
  const listas = useListasPrecio();
  const tasas = useTasas();
  const puedeEditar = puede('productos.precios');
  const historico = useHistoricoPrecios(id ?? null, puede('productos.precios', 'lectura'));
  const [editando, setEditando] = useState(false);
  const [nuevaPres, setNuevaPres] = useState<{ nombre: string; unidades: string } | null>(null);
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [minimo, setMinimo] = useState('');
  const editarProd = useEditarProducto(id ?? '');
  const crearPres = useCrearPresentacion(id ?? '');
  const editarPres = useEditarPresentacion(id ?? '');
  const guardarPrecios = useActualizarPrecios(id ?? '');
  const fijarMinimo = useFijarMinimo(id ?? '');

  const clave = (pid: string, lid: string) => `${pid}|${lid}`;

  const guardar = async () => {
    const precios = Object.entries(borrador).map(([k, v]) => {
      const [presentacion_id, lista_id] = k.split('|') as [string, string];
      return { presentacion_id, lista_id, precio_unitario_divisa: normalizarDecimal(v) };
    });
    if (precios.length === 0) return;
    try {
      const r = await guardarPrecios.mutateAsync({ precios });
      setBorrador({});
      toast.success('Precios actualizados', { description: `${precios.length} celda(s). Queda en el histórico con tu usuario.` });
      for (const a of r.advertencias) toast.warning('Precio no decreciente', { description: a, duration: 8000 });
    } catch (e) {
      toast.error('No se pudieron guardar los precios', { description: mensajeDeError(e) });
    }
  };

  return (
    <EstadoQuery cargando={producto.isLoading} error={producto.error} datos={producto.data} reintentar={() => void producto.refetch()}>
      {(p) => (
        <>
          <TituloPagina
            titulo={`${p.codigo} · ${p.nombre}`}
            subtitulo={[p.categoria, p.subcategoria, p.marca, p.color, p.unidad].filter(Boolean).join(' · ')}
            acciones={
              <>
                <Button variant="outline" onClick={() => navigate('/productos')}>Volver</Button>
                {puedeEditar ? (
                  <>
                    <Button variant="outline" onClick={() => setEditando(true)} data-testid="btn-editar-ficha">Editar ficha</Button>
                    {/* Un producto con existencia o algo en curso no se desactiva: el motivo se muestra (re-auditoría 2026-10-18, ronda 2, hallazgo 3-H10).
                        Desactivar pide confirmación (auditoría de frontend FE-06) */}
                    <BotonActivo
                      activo={p.activo}
                      titulo={`Desactivar ${p.nombre}`}
                      consecuencia="El producto deja de aparecer en el punto de venta, en las cotizaciones y en las búsquedas; su historial, sus precios y sus documentos quedan igual."
                      variante="destructive"
                      varianteActivar="default"
                      size="default"
                      cargando={editarProd.isPending}
                      alCambiar={(activo) => editarProd.mutateAsync({ activo }).then(() => { toast.success(activo ? 'Producto activado' : 'Producto desactivado'); }, (e) => { toast.error(activo ? 'No se pudo activar' : 'No se pudo desactivar', { description: mensajeDeError(e) }); })}
                      testid="btn-activar-producto"
                    />
                  </>
                ) : null}
              </>
            }
          />
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader className="flex-row items-start justify-between">
                <div>
                  <CardTitle>Presentaciones y precios ($ divisa por unidad)</CardTitle>
                  {/* Cómo salen los Bs (tasa 2) y el $ a BCV: solo la dueña (pedido de la dueña 2026-10-19) */}
                  <CardDescription>{puedeEditar ? <>{veTasa2(usuario) ? <>Bs = precio × tasa 2 {tasas ? formatearTasa(tasas.tasa_paralela) : '—'} (redondeado a céntimos) · $ a BCV = Bs ÷ {tasas ? formatearTasa(tasas.tasa_bcv) : '—'}. </> : null}Edita y guarda: cada cambio queda en el histórico.</> : 'Solo lectura para tu rol.'}</CardDescription>
                </div>
                {!puedeEditar ? <Lock className="h-4 w-4 text-muted-foreground" aria-label="Precios bloqueados para tu rol" data-testid="candado-precios" /> : null}
              </CardHeader>
              <CardContent className="space-y-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Presentación</TableHead>
                      <TableHead className="text-right">Unidades</TableHead>
                      {(listas.data ?? []).map((l) => (
                        <TableHead key={l.id} className="text-right">{l.nombre}</TableHead>
                      ))}
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {p.presentaciones.map((pres) => (
                      <TableRow key={pres.id} className={!pres.activa ? 'opacity-50' : undefined}>
                        <TableCell className="font-medium">
                          {pres.nombre} {!pres.activa ? <Badge variant="neutro">inactiva</Badge> : null}
                        </TableCell>
                        <TableCell className="text-right num">{Money.parse(pres.unidades).toDisplay()}</TableCell>
                        {(listas.data ?? []).map((l) => {
                          const actual = pres.precios.find((x) => x.lista_id === l.id)?.precio_unitario_divisa ?? '';
                          const k = clave(pres.id, l.id);
                          const valor = borrador[k] ?? actual;
                          return (
                            <TableCell key={l.id} className="text-right">
                              {puedeEditar ? (
                                <Input inputMode="decimal" value={valor} onChange={(e) => setBorrador((b) => ({ ...b, [k]: e.target.value }))} className="h-8 w-24 text-right" aria-label={`${pres.nombre} ${l.nombre}`} data-testid={`precio-${pres.unidades}-${l.codigo}`} />
                              ) : actual ? (
                                <Dinero valor={actual} modo="triple" referencial={tasas} />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          {puedeEditar && !Money.parse(pres.unidades).eq(1) ? (
                            <Button size="sm" variant="ghost" onClick={() => editarPres.mutate({ pid: pres.id, datos: { activa: !pres.activa } })}>
                              {pres.activa ? 'Inactivar' : 'Reactivar'}
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {puedeEditar ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={() => void guardar()} disabled={Object.keys(borrador).length === 0} cargando={guardarPrecios.isPending} data-testid="guardar-precios">
                      Guardar precios ({Object.keys(borrador).length})
                    </Button>
                    <Button variant="outline" onClick={() => setNuevaPres({ nombre: '', unidades: '' })}>
                      <Plus /> Presentación
                    </Button>
                    {nuevaPres ? (
                      <form
                        className="flex items-end gap-2"
                        onSubmit={(ev) => {
                          ev.preventDefault();
                          // «1.000» son mil unidades, no una (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H6)
                          crearPres.mutate(
                            { nombre: nuevaPres.nombre, unidades: normalizarDecimal(nuevaPres.unidades), orden: p.presentaciones.length + 1 },
                            { onSuccess: () => { setNuevaPres(null); toast.success('Presentación creada'); }, onError: (e) => toast.error(mensajeDeError(e)) },
                          );
                        }}
                      >
                        <Input placeholder="Nombre (Caja x12)" value={nuevaPres.nombre} onChange={(e) => setNuevaPres({ ...nuevaPres, nombre: e.target.value })} className="w-40" />
                        <Input placeholder="Unidades" inputMode="decimal" value={nuevaPres.unidades} onChange={(e) => setNuevaPres({ ...nuevaPres, unidades: e.target.value })} className="w-24" />
                        <Button type="submit" size="sm" cargando={crearPres.isPending}>Agregar</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setNuevaPres(null)}>Cancelar</Button>
                      </form>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stock · {usuario?.sucursal_nombre ?? 'sin sucursal'}</CardTitle>
                <CardDescription>disponible = existencia − pendiente por entregar</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {p.tipo === 'servicio' ? (
                  <p className="rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="ficha-servicio">Servicio: no lleva inventario. Se factura como cualquier renglón y nace entregado.</p>
                ) : p.stock_sucursal.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Almacén</TableHead>
                        <TableHead className="text-right">Exist.</TableHead>
                        <TableHead className="text-right">Pend.</TableHead>
                        <TableHead className="text-right">Dispon.</TableHead>
                        <TableHead className="text-right">Mínimo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {p.stock_sucursal.map((s) => (
                        <TableRow key={s.almacen_id} data-testid={`ficha-stock-${s.almacen_nombre}`}>
                          <TableCell>
                            {s.almacen_nombre} {s.principal ? <Badge variant="info">principal</Badge> : null}
                            {s.estado_minimo === 'bajo' ? <Badge variant="critico" className="ml-1" data-testid={`minimo-estado-${s.almacen_nombre}`}>bajo mínimo</Badge> : null}
                            {s.estado_minimo === 'cerca' ? <Badge variant="pendiente" className="ml-1" data-testid={`minimo-estado-${s.almacen_nombre}`}>cerca del mínimo</Badge> : null}
                          </TableCell>
                          <TableCell className="text-right num">{cant(s.existencia)}</TableCell>
                          <TableCell className="text-right num">{cant(s.pendiente)}</TableCell>
                          <TableCell className="text-right num font-semibold">{cant(s.disponible)}</TableCell>
                          <TableCell className="text-right num">
                            <MinimoAlmacen productoId={p.id} fila={s} editable={puede('productos.minimos')} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">Toma contexto de una sucursal para ver su stock por almacén.</p>
                )}
                {p.otras_sucursales.length > 0 ? (
                  <div className="text-xs text-muted-foreground">
                    Otras sucursales:{' '}
                    {p.otras_sucursales.map((o) => (
                      <span key={o.sucursal_id} className="mr-3 num">
                        {o.sucursal_nombre} <b>{cant(o.disponible)}</b>
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="rounded-xl bg-muted/60 p-3 text-sm">
                  <p className="mb-1 text-xs text-muted-foreground">Mínimo por sucursal</p>
                  {p.minimos.length === 0 ? <p className="text-xs">Sin mínimos definidos.</p> : null}
                  {p.minimos.map((m) => (
                    <p key={m.sucursal_id} className="num">
                      {m.sucursal_nombre}: <b>{cant(m.minimo)}</b>
                    </p>
                  ))}
                  {puede('productos.minimos') && usuario?.sucursal_id ? (
                    <form
                      className="mt-2 flex items-center gap-2"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        // Formato venezolano, como la celda del almacén: «1.000» = mil (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H6)
                        fijarMinimo.mutate({ sucursal_id: usuario.sucursal_id!, minimo: normalizarDecimal(minimo) }, { onSuccess: () => { toast.success('Mínimo actualizado'); setMinimo(''); }, onError: (e) => toast.error(mensajeDeError(e)) });
                      }}
                    >
                      <Input inputMode="decimal" placeholder={`Mínimo en ${usuario.sucursal_nombre}`} value={minimo} onChange={(e) => setMinimo(e.target.value)} className="h-8 w-40" data-testid="input-minimo" />
                      <Button type="submit" size="sm" disabled={!minimo} cargando={fijarMinimo.isPending}>Fijar</Button>
                    </form>
                  ) : null}
                </div>
                {p.costo_real !== null ? <FichaCosto productoId={p.id} /> : null}
                <p className="text-xs text-muted-foreground" data-testid="ficha-fraccion">Se vende en: {FRACCION_ETIQUETA[claveFraccion(p.fraccion_minima)].toLowerCase()}</p>
                {p.lamina_largo_mm && p.lamina_ancho_mm ? (
                  <p className="text-xs text-muted-foreground" data-testid="ficha-lamina">
                    Lámina: {p.lamina_largo_mm}x{p.lamina_ancho_mm} mm{p.lamina_espesor_mm ? ` · ${formatearCantidad(Money.parse(p.lamina_espesor_mm))} mm de espesor` : ''}
                  </p>
                ) : null}
                {p.despiece_producto_nombre ? (
                  <p className="text-xs text-muted-foreground">
                    Despiece: {p.despiece_factor} piezas → {p.despiece_producto_nombre}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            {historico.data ? (
              <Card className="xl:col-span-3">
                <CardHeader>
                  <CardTitle>Histórico de precios</CardTitle>
                  <CardDescription>Cada cambio de precio, con el anterior, el nuevo y quién lo hizo.</CardDescription>
                </CardHeader>
                <CardContent>
                  {historico.data.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin cambios registrados.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Presentación</TableHead>
                          <TableHead>Lista</TableHead>
                          <TableHead className="text-right">Anterior</TableHead>
                          <TableHead className="text-right">Nuevo</TableHead>
                          <TableHead>Usuario</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historico.data.map((hp) => (
                          <TableRow key={hp.id}>
                            <TableCell>{formatearFechaHora(hp.ocurrido_en)}</TableCell>
                            <TableCell>{hp.presentacion_nombre}</TableCell>
                            <TableCell>{hp.lista_codigo}</TableCell>
                            <TableCell className="text-right num">{hp.precio_anterior ? `$ ${Money.parse(hp.precio_anterior).toDisplay()}` : '—'}</TableCell>
                            <TableCell className="text-right num font-medium">$ {Money.parse(hp.precio_nuevo).toDisplay()}</TableCell>
                            <TableCell>{hp.usuario_nombre ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </div>
          <DialogProducto abierto={editando} cerrar={() => setEditando(false)} producto={p} />
        </>
      )}
    </EstadoQuery>
  );
}

/** Mínimo de un almacén: se escribe en la celda y se guarda con Enter o al salir. Vacío o 0 = sin alerta. */
function MinimoAlmacen({ productoId, fila, editable }: { productoId: string; fila: StockAlmacenDTO; editable: boolean }) {
  const fijar = useFijarMinimoAlmacen(productoId);
  const actual = fila.minimo === null ? '' : cant(fila.minimo);
  const [valor, setValor] = useState<string | null>(null);
  if (!editable) return <>{actual || '—'}</>;
  const guardar = () => {
    if (fijar.isPending) return;
    if (valor === null || valor.trim() === actual) {
      setValor(null);
      return;
    }
    fijar.mutate(
      { almacen_id: fila.almacen_id, minimo: normalizarDecimal(valor) || '0' },
      { onSuccess: () => { toast.success(`Mínimo de ${fila.almacen_nombre} actualizado`); setValor(null); }, onError: (e) => toast.error('No se pudo fijar el mínimo', { description: mensajeDeError(e) }) },
    );
  };
  return (
    <Input
      inputMode="decimal"
      placeholder="—"
      value={valor ?? actual}
      onChange={(e) => setValor(e.target.value)}
      onBlur={guardar}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); guardar(); } }}
      disabled={fijar.isPending}
      className="ml-auto h-7 w-16 px-1.5 text-right"
      aria-label={`Mínimo en ${fila.almacen_nombre}`}
      data-testid={`minimo-almacen-${fila.almacen_nombre}`}
    />
  );
}

export type { ProductoResumenDTO };
